'use client';

import { FC } from 'react';
import { useAnnouncements } from '@/contexts/AnnouncementsProvider';
import AnnouncementStripView from './AnnouncementStripView';
import AnnouncementToastView from './AnnouncementToastView';
import AnnouncementVideoLightbox from './AnnouncementVideoLightbox';

/**
 * Vendor-facing announcement renderer. Mounted once in ProtectedLayout. Reads the shared
 * feed from AnnouncementsProvider and renders the strip (in flow), toasts (fixed bottom-right),
 * and the single video lightbox instance. The bell/panel live in the topbar separately.
 */
const AnnouncementBanners: FC = () => {
  const {
    active, dismiss, handleCta, lightboxAnnouncement, closeLightbox,
  } = useAnnouncements();

  // Show only ONE strip and ONE toast at a time (the newest — `active` is created_at DESC) so the
  // banners never stack and pile up at the top of the page. Older items remain in the What's-new bell.
  const strip = active.find((a) => a.placement === 'strip');
  const toasts = active.filter((a) => a.placement === 'toast').slice(0, 1);

  return (
    <>
      {strip && (
        // Full-bleed in-flow banner at the very top of the content. z-[60] paints it above the
        // topbar cluster (z-50) so the strip covers the top-right controls while it's shown.
        <div className="relative z-[60]">
          <AnnouncementStripView
            announcement={strip}
            onDismiss={() => dismiss(strip.id)}
            onCta={() => handleCta(strip)}
          />
        </div>
      )}

      {toasts.length > 0 && (
        // Bottom-right, near the screen edge; below the panel (z-70) and lightbox (z-200).
        <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-3">
          {toasts.map((a) => (
            <AnnouncementToastView
              key={a.id}
              announcement={a}
              onDismiss={() => dismiss(a.id)}
              onCta={() => handleCta(a)}
            />
          ))}
        </div>
      )}

      <AnnouncementVideoLightbox announcement={lightboxAnnouncement} onClose={closeLightbox} />
    </>
  );
};

export default AnnouncementBanners;
